import React, { useMemo, useState } from 'react';
import { BarChart3Icon, StethoscopeIcon, DownloadIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Appointment, Doctor, Service } from '../../types';
import { formatCurrency, getToday, getDoctorPrefix } from '../../utils/helpers';
import { exportRevenueRankingToPdf } from '../../utils/pdfExport';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
interface RevenueByUserProps {
  appointments: Appointment[];
  getService: (id: string) => Service | undefined;
}
export function DashboardRevenueByUser({
  appointments,
  getService
}: RevenueByUserProps) {
  const {
    user
  } = useAuth();
  const userRoleNorm = user?.role?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
  const canDownloadPdf = user?.role === 'administrador' || user?.role === 'supervisor' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const [revSelectedUserId, setRevSelectedUserId] = useState('all');
  const [revDateFrom, setRevDateFrom] = useState<string>(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [revDateTo, setRevDateTo] = useState<string>(getToday());
  const revenueByUserStats = useMemo(() => {
    const filteredApts = appointments.filter(a => {
      return a.status === 'completada' && (!revDateFrom || a.date >= revDateFrom) && (!revDateTo || a.date <= revDateTo) && (revSelectedUserId === 'all' || a.createdByUserId === revSelectedUserId);
    });
    const revenueMap: Record<string, number> = {};
    filteredApts.forEach(apt => {
      const userName = apt.createdByUserName || 'Sin registrar';
      const serviceIds = apt.serviceIds && apt.serviceIds.length > 0 ? apt.serviceIds : [apt.serviceId].filter(Boolean);
      let aptTotal = 0;
      serviceIds.forEach(sId => {
        const s = getService(sId);
        if (s) aptTotal += s.price;
      });
      if (aptTotal > 0) revenueMap[userName] = (revenueMap[userName] || 0) + aptTotal;
    });
    return Object.entries(revenueMap).map(([name, amount]) => ({
      name,
      amount
    })).sort((a, b) => b.amount - a.amount);
  }, [appointments, getService, revDateFrom, revDateTo, revSelectedUserId]);
  return <Card padding="none" className="overflow-hidden" data-id="element-1980">
      <div className="h-1.5 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" data-id="element-1981" />
      <div className="p-4 md:p-6" data-id="element-1982">
        <CardHeader className="px-0 pt-0 flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1983">
          <CardTitle className="flex items-center gap-2" data-id="element-1984">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 shadow-lg shadow-emerald-500/20" data-id="element-1985">
              <BarChart3Icon className="w-5 h-5 text-white" data-id="element-1986" />
            </div>
            <div data-id="element-1987">
              <span className="block" data-id="element-1988">Ingresos por Usuario</span>
              <span className="text-xs font-normal text-gray-500 dark:text-gray-400" data-id="element-1989">
                Citas completadas
              </span>
            </div>
          </CardTitle>
          <div className="flex items-center gap-2" data-id="element-1990">
            {canDownloadPdf && revenueByUserStats.length > 0 && <button onClick={() => {
            exportRevenueRankingToPdf(revenueByUserStats, 'Ingresos por Usuario', {
              from: revDateFrom,
              to: revDateTo
            });
          }} className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors" title="Descargar PDF" data-id="element-1991">
                <DownloadIcon className="w-4 h-4" data-id="element-1992" />
              </button>}
            <Badge className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-1.5 px-3 text-sm shadow-lg shadow-emerald-500/20 border-none" data-id="element-1993">
              {formatCurrency(revenueByUserStats.reduce((sum, item) => sum + item.amount, 0))}
            </Badge>
          </div>
        </CardHeader>

        <div className="flex flex-wrap items-center gap-2 mt-4 mb-5" data-id="element-1994">
          <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-800 p-1 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-1995">
            <input type="date" className="bg-transparent text-xs border-none focus:ring-0 p-1 text-gray-700 dark:text-gray-300" value={revDateFrom} onChange={e => setRevDateFrom(e.target.value)} data-id="element-1996" />
            <span className="text-gray-300 text-xs" data-id="element-1997">→</span>
            <input type="date" className="bg-transparent text-xs border-none focus:ring-0 p-1 text-gray-700 dark:text-gray-300" value={revDateTo} onChange={e => setRevDateTo(e.target.value)} data-id="element-1998" />
          </div>
          <select className="text-xs border rounded-lg p-1.5 bg-gray-50 dark:bg-gray-800 dark:border-gray-700 focus:ring-emerald-500 text-gray-700 dark:text-gray-300" value={revSelectedUserId} onChange={e => setRevSelectedUserId(e.target.value)} data-id="element-1999">
            <option value="all" data-id="element-2000">Todos</option>
            {Array.from(new Set(appointments.map(a => a.createdByUserId).filter(Boolean))).map(userId => {
            const apt = appointments.find(a => a.createdByUserId === userId);
            return <option key={userId} value={userId} data-id="element-2001">
                  {apt?.createdByUserName || userId}
                </option>;
          })}
          </select>
        </div>

        <CardContent className="px-0 pb-0" data-id="element-2002">
          {revenueByUserStats.length > 0 && <div className="mb-6" data-id="element-2003">
              <ResponsiveContainer width="100%" height={240} data-id="element-2004">
                <BarChart data={revenueByUserStats.slice(0, 6)} layout="vertical" margin={{
              left: 0,
              right: 10
            }} data-id="element-2005">
                  <defs data-id="element-2006">
                    <linearGradient id="userRevenueGrad" x1="0" y1="0" x2="1" y2="0" data-id="element-2007">
                      <stop offset="0%" stopColor="#10b981" data-id="element-2008" />
                      <stop offset="100%" stopColor="#14b8a6" data-id="element-2009" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={true} vertical={false} data-id="element-2010" />
                  <XAxis type="number" stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} data-id="element-2011" />
                  <YAxis dataKey="name" type="category" stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} width={110} tick={{
                fill: '#374151',
                fontWeight: 500
              }} data-id="element-2012" />
                  <Tooltip contentStyle={{
                backgroundColor: 'white',
                border: 'none',
                borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
                padding: '10px 14px'
              }} formatter={(value: number) => [formatCurrency(value), 'Ingresos']} data-id="element-2013" />
                  <Bar dataKey="amount" fill="url(#userRevenueGrad)" radius={[0, 6, 6, 0]} name="Ingresos" data-id="element-2014" />
                </BarChart>
              </ResponsiveContainer>
            </div>}
          <div className="space-y-4" data-id="element-2015">
            {revenueByUserStats.map((user, index) => {
            const maxAmount = Math.max(...revenueByUserStats.map(u => u.amount), 1);
            const medals = ['🥇', '🥈', '🥉'];
            const medal = index < 3 ? medals[index] : null;
            return <div key={user.name} className="flex items-center gap-3 px-1 group" data-id="element-2016">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm ${index === 0 ? 'bg-gradient-to-br from-amber-400 to-yellow-500 text-white' : index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-white' : index === 2 ? 'bg-gradient-to-br from-orange-400 to-amber-500 text-white' : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-800'}`} data-id="element-2017">
                    {medal || index + 1}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-2018">
                    <div className="flex justify-between mb-1.5" data-id="element-2019">
                      <span className="text-[15px] font-semibold text-gray-800 dark:text-gray-200 truncate" data-id="element-2020">
                        {user.name}
                      </span>
                      <span className="text-[15px] font-bold text-emerald-600 dark:text-emerald-400 ml-2 whitespace-nowrap" data-id="element-2021">
                        {formatCurrency(user.amount)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-2.5 overflow-hidden" data-id="element-2022">
                      <div className="h-full rounded-full transition-all duration-1000 ease-out bg-gradient-to-r from-emerald-500 to-teal-400" style={{
                    width: `${user.amount / maxAmount * 100}%`
                  }} data-id="element-2023" />
                    </div>
                  </div>
                </div>;
          })}
            {revenueByUserStats.length === 0 && <div className="text-center py-10 text-gray-400 text-sm italic" data-id="element-2024">
                No se encontraron ingresos para este filtro.
              </div>}
          </div>
        </CardContent>
      </div>
    </Card>;
}
interface RevenueByDoctorProps {
  appointments: Appointment[];
  doctors: Doctor[];
  getService: (id: string) => Service | undefined;
  getDoctor: (id: string) => Doctor | undefined;
}
export function DashboardRevenueByDoctor({
  appointments,
  doctors,
  getService,
  getDoctor
}: RevenueByDoctorProps) {
  const {
    user
  } = useAuth();
  const {
    getSpecialty
  } = useData();
  const userRoleNorm = user?.role?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
  const canDownloadPdf = user?.role === 'administrador' || user?.role === 'supervisor' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const [revDoctorDateFrom, setRevDoctorDateFrom] = useState<string>(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [revDoctorDateTo, setRevDoctorDateTo] = useState<string>(getToday());
  const [revDoctorSelectedId, setRevDoctorSelectedId] = useState<string>('all');
  const revenueByDoctorStats = useMemo(() => {
    const filteredApts = appointments.filter(a => {
      return a.status === 'completada' && (!revDoctorDateFrom || a.date >= revDoctorDateFrom) && (!revDoctorDateTo || a.date <= revDoctorDateTo) && (revDoctorSelectedId === 'all' || a.doctorId === revDoctorSelectedId);
    });
    const revenueMap: Record<string, {
      name: string;
      amount: number;
      count: number;
    }> = {};
    filteredApts.forEach(apt => {
      const doctor = getDoctor(apt.doctorId);
      const doctorName = doctor ? `${getDoctorPrefix(doctor.gender, getSpecialty(doctor.specialtyId)?.name)} ${doctor.name}` : 'Sin asignar';
      const serviceIds = apt.serviceIds && apt.serviceIds.length > 0 ? apt.serviceIds : [apt.serviceId].filter(Boolean);
      let aptTotal = 0;
      serviceIds.forEach(sId => {
        const s = getService(sId);
        if (s) aptTotal += s.price;
      });
      if (aptTotal > 0) {
        if (!revenueMap[doctorName]) revenueMap[doctorName] = {
          name: doctorName,
          amount: 0,
          count: 0
        };
        revenueMap[doctorName].amount += aptTotal;
        revenueMap[doctorName].count += 1;
      }
    });
    return Object.values(revenueMap).sort((a, b) => b.amount - a.amount);
  }, [appointments, getService, getDoctor, revDoctorDateFrom, revDoctorDateTo, revDoctorSelectedId]);
  return <Card padding="none" className="overflow-hidden" data-id="element-2025">
      <div className="h-1.5 bg-gradient-to-r from-violet-500 via-purple-500 to-indigo-500" data-id="element-2026" />
      <div className="p-4 md:p-6" data-id="element-2027">
        <CardHeader className="px-0 pt-0 flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-2028">
          <CardTitle className="flex items-center gap-2" data-id="element-2029">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 shadow-lg shadow-violet-500/20" data-id="element-2030">
              <StethoscopeIcon className="w-5 h-5 text-white" data-id="element-2031" />
            </div>
            <div data-id="element-2032">
              <span className="block" data-id="element-2033">Ingresos por Doctor y Terapeuta</span>
              <span className="text-xs font-normal text-gray-500 dark:text-gray-400" data-id="element-2034">
                Citas completadas
              </span>
            </div>
          </CardTitle>
          <div className="flex items-center gap-2" data-id="element-2035">
            {canDownloadPdf && revenueByDoctorStats.length > 0 && <button onClick={() => {
            exportRevenueRankingToPdf(revenueByDoctorStats, 'Ingresos por Doctor', {
              from: revDoctorDateFrom,
              to: revDoctorDateTo
            });
          }} className="p-2 text-gray-400 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/20 rounded-lg transition-colors" title="Descargar PDF" data-id="element-2036">
                <DownloadIcon className="w-4 h-4" data-id="element-2037" />
              </button>}
            <Badge className="bg-gradient-to-r from-violet-500 to-purple-600 text-white py-1.5 px-3 text-sm shadow-lg shadow-violet-500/20 border-none" data-id="element-2038">
              {formatCurrency(revenueByDoctorStats.reduce((sum, item) => sum + item.amount, 0))}
            </Badge>
          </div>
        </CardHeader>

        <div className="flex flex-wrap items-center gap-2 mt-4 mb-5" data-id="element-2039">
          <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-800 p-1 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-2040">
            <input type="date" className="bg-transparent text-xs border-none focus:ring-0 p-1 text-gray-700 dark:text-gray-300" value={revDoctorDateFrom} onChange={e => setRevDoctorDateFrom(e.target.value)} data-id="element-2041" />
            <span className="text-gray-300 text-xs" data-id="element-2042">→</span>
            <input type="date" className="bg-transparent text-xs border-none focus:ring-0 p-1 text-gray-700 dark:text-gray-300" value={revDoctorDateTo} onChange={e => setRevDoctorDateTo(e.target.value)} data-id="element-2043" />
          </div>
          <select className="text-xs border rounded-lg p-1.5 bg-gray-50 dark:bg-gray-800 dark:border-gray-700 focus:ring-violet-500 text-gray-700 dark:text-gray-300" value={revDoctorSelectedId} onChange={e => setRevDoctorSelectedId(e.target.value)} data-id="element-2044">
            <option value="all" data-id="element-2045">Todos los doctores</option>
            {doctors.map(doc => <option key={doc.id} value={doc.id} data-id="element-2046">
                {getDoctorPrefix(doc.gender, getSpecialty(doc.specialtyId)?.name)}{' '}
                {doc.name}
              </option>)}
          </select>
        </div>

        <CardContent className="px-0 pb-0" data-id="element-2047">
          {revenueByDoctorStats.length > 0 && <div className="mb-6" data-id="element-2048">
              <ResponsiveContainer width="100%" height={240} data-id="element-2049">
                <BarChart data={revenueByDoctorStats.slice(0, 6)} layout="vertical" margin={{
              left: 0,
              right: 10
            }} data-id="element-2050">
                  <defs data-id="element-2051">
                    <linearGradient id="doctorRevenueGrad" x1="0" y1="0" x2="1" y2="0" data-id="element-2052">
                      <stop offset="0%" stopColor="#8b5cf6" data-id="element-2053" />
                      <stop offset="100%" stopColor="#6366f1" data-id="element-2054" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={true} vertical={false} data-id="element-2055" />
                  <XAxis type="number" stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} data-id="element-2056" />
                  <YAxis dataKey="name" type="category" stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} width={120} tick={{
                fill: '#374151',
                fontWeight: 500
              }} data-id="element-2057" />
                  <Tooltip contentStyle={{
                backgroundColor: 'white',
                border: 'none',
                borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
                padding: '10px 14px'
              }} formatter={(value: number) => [formatCurrency(value), 'Ingresos']} data-id="element-2058" />
                  <Bar dataKey="amount" fill="url(#doctorRevenueGrad)" radius={[0, 6, 6, 0]} name="Ingresos" data-id="element-2059" />
                </BarChart>
              </ResponsiveContainer>
            </div>}
          <div className="space-y-4" data-id="element-2060">
            {revenueByDoctorStats.map((doctor, index) => {
            const maxAmount = Math.max(...revenueByDoctorStats.map(d => d.amount), 1);
            const medals = ['🥇', '🥈', '🥉'];
            const medal = index < 3 ? medals[index] : null;
            return <div key={doctor.name} className="flex items-center gap-3 px-1 group" data-id="element-2061">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm ${index === 0 ? 'bg-gradient-to-br from-amber-400 to-yellow-500 text-white' : index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-white' : index === 2 ? 'bg-gradient-to-br from-orange-400 to-amber-500 text-white' : 'bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 border border-violet-100 dark:border-violet-800'}`} data-id="element-2062">
                    {medal || index + 1}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-2063">
                    <div className="flex justify-between mb-1.5" data-id="element-2064">
                      <div className="flex items-center gap-2 min-w-0" data-id="element-2065">
                        <span className="text-[15px] font-semibold text-gray-800 dark:text-gray-200 truncate" data-id="element-2066">
                          {doctor.name}
                        </span>
                        <span className="text-[11px] text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded-full whitespace-nowrap flex-shrink-0" data-id="element-2067">
                          {doctor.count} citas
                        </span>
                      </div>
                      <span className="text-[15px] font-bold text-violet-600 dark:text-violet-400 ml-2 whitespace-nowrap" data-id="element-2068">
                        {formatCurrency(doctor.amount)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-2.5 overflow-hidden" data-id="element-2069">
                      <div className="h-full rounded-full transition-all duration-1000 ease-out bg-gradient-to-r from-violet-500 to-indigo-400" style={{
                    width: `${doctor.amount / maxAmount * 100}%`
                  }} data-id="element-2070" />
                    </div>
                  </div>
                </div>;
          })}
            {revenueByDoctorStats.length === 0 && <div className="text-center py-10 text-gray-400 text-sm italic" data-id="element-2071">
                No se encontraron ingresos de doctores para este período.
              </div>}
          </div>
        </CardContent>
      </div>
    </Card>;
}